import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Wallet, AlertTriangle, CircleDollarSign, Link2, Clock } from "lucide-react";
import api from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtMoney0 } from "@/lib/format";
import { FUND_META, CORE_FUNDS, SPEND_SEGMENTS, pct } from "@/lib/budget";

function StackedBar({ fund, segments, budget, mode }) {
  const meta = FUND_META[fund];
  const committed = SPEND_SEGMENTS.reduce((s, seg) => s + (segments?.[seg.key] || 0), 0);
  if (fund === "recording" && mode === "covered_by_advance") {
    return (
      <div data-testid={`fundbar-${fund}`} className="flex items-center justify-between text-xs">
        <span className="font-medium text-neutral-600">{meta.label}</span>
        <span className="text-neutral-400">None — covered by advance</span>
      </div>
    );
  }
  return (
    <div data-testid={`fundbar-${fund}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-neutral-600">{meta.label}</span>
        <span className="num-display text-neutral-500">
          {segments == null ? "—" : fmtMoney0(committed)}<span className="text-neutral-400"> / {budget ? fmtMoney0(budget) : "—"}</span>
        </span>
      </div>
      <div className="mt-1 flex h-2 overflow-hidden rounded-full bg-neutral-200/70">
        {segments != null && SPEND_SEGMENTS.map((seg) => {
          const w = pct(segments[seg.key] || 0, budget);
          return w > 0 ? <div key={seg.key} title={`${seg.label}: ${fmtMoney0(segments[seg.key])}`} style={{ width: `${w}%`, backgroundColor: seg.color }} /> : null;
        })}
      </div>
    </div>
  );
}

export default function BudgetTrackers() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/budget/artists").then((r) => setData(r.data)).catch(() => toast.error("Failed to load budgets"));
  }, []);

  const artists = data?.artists || [];
  const lastUpdated = data?.last_updated || "2026-10-09T00:00:00Z";

  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="flex items-center gap-2 text-sm font-medium text-neutral-500">
              <Wallet className="h-4 w-4" /> Artist portal · Admin only
            </p>
            <h1 className="mt-1 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Budget Trackers</h1>
            <p className="mt-2 max-w-xl text-neutral-500">
              Contract budget vs. spend by album for each artist. Recording, Marketing and Artist Advance — legal tracked separately, outside the funds.
            </p>
          </div>
          {lastUpdated && (
            <div data-testid="budget-snapshot-note" className="flex items-center gap-2 rounded-full border border-[#E7DFCC] bg-[#FFFDF8] px-3 py-1.5 text-xs text-neutral-500">
              <Clock className="h-3.5 w-3.5" /> Last updated {new Date(lastUpdated).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </div>
          )}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {data === null && Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-80 rounded-2xl" />)}

          {artists.map((a, i) => (
            <motion.button
              key={a.id}
              data-testid={`budget-artist-card-${a.id}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -5 }}
              onClick={() => navigate(`/budget/${a.id}`)}
              className="pb-card flex flex-col gap-4 p-5 text-left"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate font-display text-xl font-bold">{a.name}</h3>
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-neutral-500">
                    {a.linked_artist_id ? (
                      <><Link2 className="h-3 w-3" /> Linked to roster profile</>
                    ) : (
                      <>Budget-only artist</>
                    )}
                  </p>
                </div>
                {a.needs_review_count > 0 && (
                  <span data-testid={`needs-review-${a.id}`} className="flex shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                    <AlertTriangle className="h-3 w-3" /> {a.needs_review_count} to review
                  </span>
                )}
              </div>

              <div className="flex items-end justify-between rounded-xl bg-[#F7EFDD]/70 px-4 py-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-neutral-500">Contract budget</p>
                  <p className="num-display text-2xl font-extrabold">{fmtMoney0(a.budget_total_cents)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] uppercase tracking-wide text-neutral-500">Committed</p>
                  <p className="num-display text-2xl font-extrabold text-[#35606E]">
                    {!a.spend_tracked ? "Not tracked" : (a.reconciled || a.committed_total_cents ? fmtMoney0(a.committed_total_cents) : "Unreconciled")}
                  </p>
                </div>
              </div>

              {a.contract_unconfirmed && (
                <span data-testid={`unconfirmed-${a.id}`} className="inline-flex w-fit items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-orange-700">
                  <AlertTriangle className="h-3 w-3" /> Needs review — unconfirmed contract
                </span>
              )}

              {a.spend_tracked && (
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-neutral-500">
                  {SPEND_SEGMENTS.map((seg) => (
                    <span key={seg.key} className="inline-flex items-center gap-1">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: seg.color }} />
                      {seg.label.split(" ")[0]} {fmtMoney0(
                        seg.key === "paid_cents" ? a.paid_total_cents : seg.key === "pending_cents" ? a.pending_total_cents : a.bookmarked_total_cents
                      )}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex flex-col gap-3">
                {CORE_FUNDS.map((f) => (
                  <StackedBar key={f} fund={f} budget={a.budget[f]} mode={a.recording_mode} segments={a.spend_tracked ? a.funds[f] : (a.budget[f] ? { paid_cents: 0, pending_cents: 0, bookmarked_cents: 0 } : null)} />
                ))}
              </div>

              {!a.spend_tracked && (
                <p className="flex items-center gap-1.5 text-[11px] text-neutral-500">
                  <CircleDollarSign className="h-3.5 w-3.5" /> Contract budget caps — spend not yet tracked.
                </p>
              )}
            </motion.button>
          ))}
        </div>

        {data?.unreconciled?.length > 0 && (
          <section data-testid="unreconciled-panel" className="mt-12 rounded-2xl border border-amber-200 bg-amber-50/60 p-5">
            <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
              <AlertTriangle className="h-4 w-4" /> Needs review — unreconciled contracts ({data.unreconciled.length})
            </p>
            <p className="mt-1 text-xs text-amber-900/70">Mentioned in the source inboxes but no executed budget agreement was extracted. Not shown as artists until confirmed.</p>
            <ul className="mt-3 space-y-2 text-sm text-amber-900/90">
              {data.unreconciled.map((u, i) => (
                <li key={i} className="flex gap-2">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" />
                  <span><strong>{u.artist}</strong> — {u.reason}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
